import { formatCpmCount } from '../../../constants/centralProductMaster.constants';
import type { CpmMasterDetailsResponse } from '../../../types/centralProductMaster.types';

type StateRow = {
    _id?: string;
    state?: string;
    retailPrice?: number | null;
    purchasePrice?: number | null;
    wholesalePrice?: number | null;
    distilleryPrice?: number | null;
    exciseCategoryName?: string | null;
    exciseSubCategoryName?: string | null;
    itemCode?: string | null | string[];
};

function formatMoney(n: number | null | undefined): string {
    if (n == null || Number.isNaN(Number(n))) return '—';
    return `₹${formatCpmCount(Number(n))}`;
}

function itemCodeOf(sp: StateRow | undefined): string {
    const v = sp?.itemCode;
    if (Array.isArray(v)) return v.filter(Boolean).join(', ') || '—';
    return v ? String(v) : '—';
}

function spFields(sp: StateRow | undefined) {
    return [
        { key: 'retail', label: 'Retail price', value: formatMoney(sp?.retailPrice) },
        { key: 'purchase', label: 'Purchase price', value: formatMoney(sp?.purchasePrice) },
        {
            key: 'wholesale',
            label: 'Wholesale price',
            value: sp?.wholesalePrice != null ? formatMoney(sp.wholesalePrice) : '—',
        },
        { key: 'distillery', label: 'Distillery price', value: formatMoney(sp?.distilleryPrice) },
        { key: 'excise', label: 'Excise category', value: sp?.exciseCategoryName || '—' },
        { key: 'exciseSub', label: 'Subcategory', value: sp?.exciseSubCategoryName || '—' },
        { key: 'itemCode', label: 'Item code', value: itemCodeOf(sp) },
    ];
}

function findSp(list: StateRow[] | undefined, state: string): StateRow | undefined {
    return (list ?? []).find((sp) => String(sp.state ?? '') === state);
}

function trunc(s: string, max = 26): string {
    if (s.length <= max) return s;
    return `${s.slice(0, max - 2)}…`;
}

function RadioDot({ chosen }: { chosen: boolean }) {
    return (
        <span
            className="inline-flex items-center justify-center shrink-0"
            style={{
                width: 17,
                height: 17,
                borderRadius: 999,
                border: `1.5px solid ${chosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                background: chosen ? 'var(--cpm-brand-purple)' : '#fff',
            }}
        >
            {chosen ? (
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3.4"
                >
                    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ) : null}
        </span>
    );
}

export interface VariantStateCollisionsProps {
    sideA: CpmMasterDetailsResponse;
    sideB: CpmMasterDetailsResponse;
    idA: string;
    idB: string;
    survivorId: string | null;
    /** Maps state → winning side id (idA or idB). */
    stateChoices: Record<string, string>;
    onPickState: (state: string, sideId: string) => void;
}

export function variantCollisionsResolved(
    sideA: CpmMasterDetailsResponse | null,
    sideB: CpmMasterDetailsResponse | null,
    stateChoices: Record<string, string>
): boolean {
    if (!sideA || !sideB) return true;
    const a = (sideA.stateProducts ?? []) as StateRow[];
    const b = (sideB.stateProducts ?? []) as StateRow[];
    const names = Array.from(
        new Set([...a, ...b].map((sp) => String(sp.state ?? '')).filter(Boolean))
    );
    const colliding = names.filter((s) => findSp(a, s) && findSp(b, s));
    return colliding.every((s) => !!stateChoices[s]);
}

export function buildVariantStateChoicesPayload(
    stateChoices: Record<string, string>,
    survivorId: string
): Record<string, 'survivor' | 'loser'> {
    const out: Record<string, 'survivor' | 'loser'> = {};
    for (const [state, sideId] of Object.entries(stateChoices)) {
        out[state] = sideId === survivorId ? 'survivor' : 'loser';
    }
    return out;
}

export default function VariantStateCollisions({
    sideA,
    sideB,
    idA,
    idB,
    survivorId,
    stateChoices,
    onPickState,
}: VariantStateCollisionsProps) {
    const a = (sideA.stateProducts ?? []) as StateRow[];
    const b = (sideB.stateProducts ?? []) as StateRow[];
    const names = Array.from(
        new Set([...a, ...b].map((sp) => String(sp.state ?? '')).filter(Boolean))
    );
    const colliding = names.filter((s) => findSp(a, s) && findSp(b, s));
    const carry = names.filter((s) => !colliding.includes(s));
    if (colliding.length === 0) return null;

    const unresolved = colliding.filter((s) => !stateChoices[s]);
    const nameOf = (id: string) => String((id === idA ? sideA : sideB).record?.name ?? id);

    const subhead =
        unresolved.length > 0
            ? unresolved.length === 1
                ? `1 of ${formatCpmCount(colliding.length)} colliding state${colliding.length === 1 ? '' : 's'} still needs a decision — only one pricing record can survive per state.`
                : `${formatCpmCount(unresolved.length)} of ${formatCpmCount(colliding.length)} colliding states still need a decision — only one pricing record can survive per state.`
            : 'All colliding states resolved. Every other state carries over untouched.';

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
            style={{
                borderRadius: 16,
                padding: '20px 22px',
                gap: 16,
                boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
            }}
        >
            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    State pricing collisions
                </span>
                <span
                    className="text-pretty"
                    style={{
                        fontSize: 13,
                        fontWeight: unresolved.length > 0 ? 700 : 600,
                        color:
                            unresolved.length > 0
                                ? 'var(--cpm-warning-text)'
                                : 'var(--cpm-text-secondary)',
                    }}
                >
                    {subhead}
                </span>
            </div>

            {carry.length > 0 ? (
                <div
                    className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                    style={{ gap: 8, padding: '13px 15px', borderRadius: 12 }}
                >
                    <span
                        className="text-[var(--cpm-text-primary)]"
                        style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        {carry.length === 1
                            ? '1 state is priced by only one record — it carries over unchanged, nothing to decide'
                            : `${formatCpmCount(carry.length)} states are priced by only one record — they carry over unchanged, nothing to decide`}
                    </span>
                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                        {carry.map((state) => {
                            const onA = !!findSp(a, state);
                            const ownerId = onA ? idA : idB;
                            return (
                                <span
                                    key={state}
                                    className="inline-flex items-center bg-white border border-[var(--cpm-border-default)]"
                                    style={{
                                        gap: 6,
                                        padding: '4px 10px',
                                        borderRadius: 999,
                                        fontSize: 11.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    {state}
                                    <span
                                        className="text-[var(--cpm-text-tertiary)] whitespace-nowrap"
                                        style={{ fontWeight: 700 }}
                                    >
                                        {survivorId
                                            ? ownerId === survivorId
                                                ? 'from the survivor'
                                                : 'from the retiring record'
                                            : `on ${trunc(nameOf(ownerId))}`}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-col" style={{ gap: 14 }}>
                {colliding.map((state) => {
                    const chosen = stateChoices[state] || null;
                    const done = !!chosen;
                    const pa = findSp(a, state);
                    const pb = findSp(b, state);
                    const opts = [
                        { sideId: idA, sp: pa, name: nameOf(idA) },
                        { sideId: idB, sp: pb, name: nameOf(idB) },
                    ];
                    return (
                        <div
                            key={state}
                            className="flex flex-col"
                            style={{
                                gap: 10,
                                padding: '14px 15px',
                                borderRadius: 12,
                                border: `1px solid ${done ? 'var(--cpm-border-default)' : 'var(--cpm-warning-border)'}`,
                                background: done
                                    ? 'var(--cpm-surface-subtle)'
                                    : 'var(--cpm-warning-bg)',
                            }}
                        >
                            <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                <span
                                    className="text-[var(--cpm-brand-navy)]"
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {state}
                                </span>
                                <span
                                    className="inline-flex items-center rounded-full uppercase"
                                    style={{
                                        padding: '3px 9px',
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        background: done
                                            ? 'var(--cpm-success-bg)'
                                            : 'var(--cpm-warning-bg)',
                                        border: `1px solid ${done ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                        color: done
                                            ? 'var(--cpm-success-text-light)'
                                            : 'var(--cpm-warning-text)',
                                    }}
                                >
                                    {done ? 'Resolved' : 'Decision needed'}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{ fontSize: 12, fontWeight: 600 }}
                                >
                                    {done
                                        ? 'The record you picked stays; the other is dropped for this state.'
                                        : 'Both records price this state — pick the one that survives.'}
                                </span>
                            </div>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: 10,
                                    alignItems: 'start',
                                }}
                            >
                                {opts.map((opt) => {
                                    const isChosen = chosen === opt.sideId;
                                    const role =
                                        survivorId == null
                                            ? 'none'
                                            : survivorId === opt.sideId
                                              ? 'survivor'
                                              : 'retiring';
                                    const other = opt.sideId === idA ? pb : pa;
                                    const fields = spFields(opt.sp);
                                    const otherFields = spFields(other);
                                    return (
                                        <button
                                            key={opt.sideId}
                                            type="button"
                                            onClick={() => onPickState(state, opt.sideId)}
                                            className="flex flex-col text-left bg-white"
                                            style={{
                                                gap: 9,
                                                padding: '13px 14px',
                                                borderRadius: 12,
                                                border: `1.5px solid ${isChosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                                                background: isChosen
                                                    ? 'rgba(110,100,240,.07)'
                                                    : '#fff',
                                                boxShadow: isChosen
                                                    ? '0 0 0 3px rgba(110,100,240,.20)'
                                                    : undefined,
                                            }}
                                        >
                                            <div className="flex items-center" style={{ gap: 8 }}>
                                                <RadioDot chosen={isChosen} />
                                                <span
                                                    className="min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {opt.name}
                                                </span>
                                                <span
                                                    className="ml-auto inline-flex items-center rounded-full uppercase shrink-0"
                                                    style={{
                                                        padding: '2px 8px',
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.05em',
                                                        background:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-purple-tint-13)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-bg)'
                                                                  : 'var(--cpm-surface-skeleton)',
                                                        border:
                                                            role === 'retiring'
                                                                ? '1px solid var(--cpm-shortfall-border)'
                                                                : undefined,
                                                        color:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-brand-purple)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-text)'
                                                                  : 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    {role === 'survivor'
                                                        ? 'Survivor'
                                                        : role === 'retiring'
                                                          ? 'Retiring record'
                                                          : 'On file'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                {fields.map((f, i) => {
                                                    const differs =
                                                        f.value !== otherFields[i]?.value;
                                                    return (
                                                        <div
                                                            key={f.key}
                                                            className="flex items-center justify-between"
                                                            style={{
                                                                gap: 10,
                                                                padding: '6px 0',
                                                                borderBottom:
                                                                    i < fields.length - 1
                                                                        ? '1px solid var(--cpm-border-default)'
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <span
                                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                                style={{
                                                                    fontSize: 10.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.05em',
                                                                }}
                                                            >
                                                                {f.label}
                                                            </span>
                                                            <span
                                                                className="tabular text-right"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                    color: differs
                                                                        ? 'var(--cpm-brand-navy)'
                                                                        : 'var(--cpm-text-secondary)',
                                                                    background: differs
                                                                        ? 'rgba(110,100,240,.09)'
                                                                        : undefined,
                                                                    borderRadius: differs
                                                                        ? 6
                                                                        : undefined,
                                                                    padding: differs
                                                                        ? '1px 6px'
                                                                        : undefined,
                                                                }}
                                                            >
                                                                {f.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
